import { useState, useCallback, useContext } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, HomeIcon, PencilSquareIcon, InformationCircleIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { ChatContext } from '../context/ChatContext';
import { useAuth } from "../../../../context/AuthContext"; 
import edamsApi from "../../../../models/edamsModel";

const CustomerSearchModal = ({ isOpen, onClose }) => {
  const { data: chatData } = useContext(ChatContext);
  const [accountNumber, setAccountNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [commentStatus, setCommentStatus] = useState({ message: '', type: '' });

  const fetchCustomerData = useCallback(async (accNum) => {
    if (!accNum.trim()) {
      setError("Account number cannot be empty.");
      setCustomerData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    setCustomerData(null);

    const baseAccountDataStructure = {
      account: {},
      balance: 0,
      balance_data: {},
      comments: [],
      connection_status: 0,
      location: [],
      transactions: [],
      consumption: null, // Add new field for consumption data
    };
    let fetched_customer_data = {};

    try {
      const formData = new FormData();
      // Use edamsApi instead of direct axios calls
      // 1. Get account info
      const response = await edamsApi.getAccountDetails(accNum);
      if (response.status == 1) {
        fetched_customer_data.account = response.account;

        // 3. Get balance & transactions (simplified for modal)
        const today = new Date().toISOString().split('T')[0];
        const balanceRes = await edamsApi.getBalanceInfo(accNum, today);
        if (balanceRes.status == 1) {
          fetched_customer_data.balance_data = balanceRes;
          fetched_customer_data.balance = parseFloat(balanceRes.last_statement.CL_BLNCE).toFixed(2);
        }

        // 4. Get comments (simplified - maybe just latest or count)
        const commentsRes = await edamsApi.getAccountComments(accNum, chatData.user?.name || "System");
        if (commentsRes.status == 1 && commentsRes.comments) {
          fetched_customer_data.comments = commentsRes.comments.reverse().slice(0, 3); // Show latest 3
        }

        // 5. Get account status
        const statusRes = await edamsApi.getAccountStatus(accNum);
        // Ensure CON_STATUS exists and parse it to an integer
        if (statusRes.status == 1 && statusRes.account_status && statusRes.account_status.CON_STATUS !== undefined) {
          fetched_customer_data.connection_status = parseInt(statusRes.account_status.CON_STATUS, 10);
        } else {
          fetched_customer_data.connection_status = 0; // Default to a non-connected state (e.g., 0 or 2 for disconnected)
        }

        // 6. Get Consumption/Graph Data
        const graphRes = await edamsApi.getGraphData(accNum, today);
        if (graphRes.data) {
          let consumption;
          if (typeof graphRes.data === 'string') {
            try {
              consumption = JSON.parse(graphRes.data);
            } catch (e) {
              console.error("Error parsing consumption data (string):", e);
              console.error("Raw consumption response (string):", graphRes.data);
              // Set an error or handle as appropriate, e.g., fetched_customer_data.consumption = { error: "Invalid format" };
            }
          } else if (typeof graphRes.data === 'object') {
            consumption = graphRes.data; // Assume it's already a parsed object
          }

          if (Array.isArray(consumption) && consumption.length > 0) {
            const consumptionTableData = consumption.map(item => ({
              date_to: item.date_to ? item.date_to.substring(0, 10) : 'N/A',
              consump: item.consump ? `${String(item.consump).split(".")[0]}kl` : 'N/A',
              bilng_date: item.bilng_date ? item.bilng_date.substring(0, 10) : 'N/A',
            }));
            fetched_customer_data.consumption = { table_data: consumptionTableData };
          } else if (consumption) { // It was parsed/an object but not a non-empty array
            console.warn("Consumption data received but is not a non-empty array:", consumption);
            }
        }
        setCustomerData({ ...baseAccountDataStructure, ...fetched_customer_data });
      } else {
        setError(response.data.message || "Customer not found or error fetching data.");
      }
    } catch (err) {
      console.error("Error fetching customer data:", err);
      setError("An error occurred while fetching customer data.");
    } finally {
      setIsLoading(false);
    }
  }, [chatData.user?.name]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomerData(accountNumber);
  };

  const handleCommentSubmitInModal = async (e) => {
    e.preventDefault();
    if (!newCommentText.trim()) {
      setCommentStatus({ message: "Comment cannot be empty.", type: "error" });
      return;
    }
    if (!customerData || !customerData.account || !customerData.account.ACCOUNT_NO) {
      setCommentStatus({ message: "Customer data not loaded. Cannot submit comment.", type: "error" });
      return;
    }

    const formData = new FormData();
    // Use edams_user if available from fetched data, otherwise fallback to chatData.user.name
    const commentUser = customerData.account.edams_user || chatData.user?.name || "System";

    setCommentStatus({ message: "Submitting...", type: "loading" });
    try {
      const response = await edamsApi.addAccountComment(
        customerData.account.ACCOUNT_NO,
        newCommentText,
        commentUser,
        new Date().toISOString()
      );
      if (response && response.status == 1) {
        setCommentStatus({ message: response.message || "Comment submitted successfully!", type: "success" });
        setNewCommentText("");
        // Optionally, re-fetch customer data to show the new comment
        fetchCustomerData(customerData.account.ACCOUNT_NO);
      } else {
        setCommentStatus({ message: response.message || "Failed to submit comment.", type: "error" });
      }
    } catch (err) {
      console.error("Error submitting comment from modal:", err);
      setCommentStatus({ message: "An error occurred while submitting the comment.", type: "error" });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-start pt-10">
      <div className="relative mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white"> {/* Reverted to max-w-2xl, adjusted padding */}
        <div className="flex justify-between items-center mb-4 pb-3 border-b"> {/* Adjusted padding and border */}
          <h3 className="text-xl font-semibold text-gray-900">Customer Lookup</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSearch} className="flex items-center gap-3 mb-6"> {/* Increased gap */}
          <input // Search input
            type="text"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            placeholder="Enter Account Number"
            className="flex-grow p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          />
          <button
            type="submit"
            disabled={isLoading}
            className="bg-sky-500 hover:bg-sky-600 text-white font-semibold py-2 px-4 rounded-md disabled:opacity-50 flex items-center"
          >
            {isLoading ? (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
            )}
            Search
          </button>
        </form>

        {error && <p className="text-red-500 text-center mb-4">{error}</p>}

        {customerData && customerData.account && Object.keys(customerData.account).length > 0 && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Customer Name Header */}
            <h4 className="text-lg font-bold text-gray-900 border-b pb-2">
              {customerData.account.CUSTOMER_NAME || `${customerData.account.TITLE || ''} ${customerData.account.INITIAL || ''} ${customerData.account.SURNAME || ''}`.trim()}
            </h4>
            
            {/* Customer Details Card */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 text-xs font-medium">Account Number</p>
                  <p className="text-gray-900 font-semibold">{customerData.account?.CUSTKEY || customerData.account?.ACCOUNT_NO || accountNumber}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs font-medium">Balance</p>
                  <p className={`font-bold ${parseFloat(customerData.balance) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    R{customerData.balance}
                  </p>
                </div>
                {(customerData.account?.CELL_TEL_NO || customerData.account?.CELL_TEL) && (
                  <div>
                    <p className="text-gray-500 text-xs font-medium">Cell Phone</p>
                    <p className="text-gray-900">{customerData.account.CELL_TEL_NO || customerData.account.CELL_TEL}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500 text-xs font-medium">Status</p>
                  <p className={`font-semibold ${customerData.connection_status === 1 ? 'text-green-600' : 'text-red-600'}`}>
                    {customerData.connection_status === 1 ? '● Connected' : customerData.connection_status === 2 ? '● Disconnected' : '● Unknown'}
                  </p>
                </div>
                {customerData.account?.EMAIL && (
                  <div className="col-span-2">
                    <p className="text-gray-500 text-xs font-medium">Email</p>
                    <p className="text-gray-900">{customerData.account.EMAIL}</p>
                  </div>
                )}
              </div>
              
              {/* Addresses */}
              <div className="border-t pt-3 space-y-2">
                <div>
                  <p className="text-gray-500 text-xs font-medium mb-1">Physical Address</p>
                  <p className="text-gray-900 text-sm">
                    {[
                      customerData.account.UA_ADRESS1,
                      customerData.account.UA_ADRESS2,
                      customerData.account.UA_ADRESS3,
                      customerData.account.UA_ADRESS4,
                      customerData.account.UA_ADRESS5
                    ].filter(Boolean).join(', ') || 'N/A'}
                  </p>
                </div>
                {(customerData.account?.POSTAL_ADRESS1 || customerData.account?.POSTAL_ADDRESS) && (
                  <div>
                    <p className="text-gray-500 text-xs font-medium mb-1">Postal Address</p>
                    <p className="text-gray-900 text-sm">
                      {[
                        customerData.account.POSTAL_ADRESS1,
                        customerData.account.POSTAL_ADRESS2,
                        customerData.account.POSTAL_ADRESS3,
                        customerData.account.POSTAL_ADRESS4,
                        customerData.account.POSTAL_ADRESS5
                      ].filter(Boolean).join(', ') || customerData.account.POSTAL_ADDRESS || 'N/A'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Consumption Data */}
            {customerData.consumption && customerData.consumption.table_data && customerData.consumption.table_data.length > 0 && (
              <div className="bg-white rounded-lg border p-4">
                <h5 className="text-sm font-semibold text-gray-700 mb-3">Consumption History</h5>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">End Date</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Consumption</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Billing Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {customerData.consumption.table_data.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-3 py-2">{item.date_to}</td>
                          <td className="px-3 py-2 font-medium">{item.consump}</td>
                          <td className="px-3 py-2">{item.bilng_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Comments */}
            {customerData.comments && customerData.comments.length > 0 && (
              <div className="bg-white rounded-lg border p-4">
                <h5 className="text-sm font-semibold text-gray-700 mb-3">Recent Comments</h5>
                <ul className="space-y-2 text-xs">
                  {customerData.comments.map(comment => (
                    <li key={comment.EVENT_ID} className="p-2 bg-gray-50 rounded">
                      <div className="flex justify-between mb-1">
                        <span className="font-semibold text-gray-700">{comment["Created By"]}</span>
                        <span className="text-gray-500">{comment["Created on"].substring(0,10)}</span>
                      </div>
                      <p className="text-gray-600">{comment.Comment}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Add Comment Section */}
            <div className="bg-white rounded-lg border p-4">
              <h5 className="text-sm font-semibold text-gray-700 mb-3">Add New Comment</h5>
              <form onSubmit={handleCommentSubmitInModal} className="space-y-3">
                <textarea
                  rows={3}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  placeholder="Type your comment here..."
                  value={newCommentText}
                  onChange={(e) => setNewCommentText(e.target.value)}
                />
                {commentStatus.message && (
                  <p className={`text-xs ${commentStatus.type === 'error' ? 'text-red-500' : commentStatus.type === 'success' ? 'text-green-500' : 'text-gray-500'}`}>
                    {commentStatus.message}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={commentStatus.type === 'loading'}
                  className="bg-sky-500 hover:bg-sky-600 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50 text-sm w-full">
                  {commentStatus.type === 'loading' ? 'Submitting...' : 'Submit Comment'}
                </button>
              </form>
            </div>
          </div>
        )}
        {customerData && !(customerData.account && Object.keys(customerData.account).length > 0) && !isLoading && !error && (
            <p className="text-gray-500 text-center">No customer data found for this account number.</p>
        )}
      </div>
    </div>
  );
};

export default CustomerSearchModal;
